/**
 * The two picture-first filter grids (2026-09-29 owner ask: "I love the way
 * you made type look in filter, replicate that and the new region into the
 * other filter places like Dex and trade pages").
 *
 *   TypeFilterGrid   → the 18 types six across, three down, each type drawn
 *                      as itself with its name under it (multi-select).
 *   RegionFilterGrid → the regions six across, two down, compact chips;
 *                      GO's region-less bucket reads "Unknown" ("Unidentified"
 *                      cost a whole third row) (single-select).
 *
 * Test ids stay `${testIdPrefix}-${key}` so every page's pins hold.
 */
import { FilterChip } from "@/components/DexFilterMenu";
import { TypeIcon } from "@/components/TypeIcon";
import { REGION_FILTER_LABELS, REGION_FILTER_ORDER, type RegionFilter } from "@/lib/dexGridFilter";
import { ALL_TYPES, type PokeType } from "@/lib/types";

export function TypeFilterGrid({
  selected,
  onToggle,
  testIdPrefix,
}: {
  selected: readonly PokeType[];
  onToggle: (type: PokeType) => void;
  testIdPrefix: string;
}) {
  return (
    <div className="grid grid-cols-6 gap-1" data-testid={`${testIdPrefix}-grid`}>
      {ALL_TYPES.map((t) => {
        const active = selected.includes(t.key);
        return (
          <button
            key={t.key}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(t.key)}
            data-testid={`${testIdPrefix}-${t.key}`}
            className="rounded-lg py-1 flex flex-col items-center gap-0.5 min-w-0"
            style={{
              background: active ? `color-mix(in srgb, ${t.color} 38%, transparent)` : "var(--surface-dark-9)",
              border: `${active ? 2 : 1}px solid ${active ? t.color : "var(--accent-fill-glow-subtle)"}`,
            }}
          >
            <TypeIcon type={t.key} size={16} title="" />
            <span
              className="text-[8px] font-bold leading-none truncate max-w-full"
              style={{ color: active ? "var(--text-ink-bright)" : "var(--text-ink-faint)" }}
            >
              {t.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function RegionFilterGrid({
  region,
  onSelect,
  testIdPrefix,
}: {
  region: RegionFilter;
  onSelect: (region: RegionFilter) => void;
  testIdPrefix: string;
}) {
  return (
    <div className="grid grid-cols-6 gap-1" data-testid={`${testIdPrefix}-grid`}>
      {REGION_FILTER_ORDER.map((r) => (
        <FilterChip
          key={r}
          label={r === "unidentified" ? "Unknown" : REGION_FILTER_LABELS[r]}
          active={region === r}
          onClick={() => onSelect(r)}
          testId={`${testIdPrefix}-${r}`}
          compact
        />
      ))}
    </div>
  );
}
